/**
 * Authentication Performance Monitor
 *
 * Tracks and reports performance metrics for all authentication operations
 * to ensure <50ms average response times and optimal user experience.
 *
 * Features:
 * - Operation timing with high-resolution timestamps
 * - Memory usage tracking
 * - Performance budgets and alerts
 * - Statistical analysis (p50, p95, p99)
 * - Performance degradation detection
 *
 * Created: September 22, 2025
 * Part of: Enterprise JWT Implementation Phase 3
 */

class AuthPerformanceMonitor {
  constructor() {
    // Performance metrics storage
    this.metrics = {
      operations: new Map(), // operation name -> array of measurements
      memory: [],
      errors: [],
      slowOperations: []
    };

    // Performance budgets (milliseconds)
    this.budgets = {
      tokenRetrieval: 10,      // Getting token from storage
      tokenValidation: 20,     // Validating JWT signature
      tokenRefresh: 200,       // Refreshing expired token
      login: 500,              // Complete login flow
      logout: 50,              // Logout operation
      sessionCheck: 5,         // Session status check
      encryption: 15,          // Token encryption
      decryption: 15,          // Token decryption
      overall: 50              // Average for all operations
    };

    // Statistics
    this.stats = {
      totalOperations: 0,
      slowOperations: 0,
      errors: 0,
      averageTime: 0
    };

    // Initialize performance observer if available
    this.initializeObserver();
  }

  /**
   * Initialize Performance Observer for detailed metrics
   */
  initializeObserver() {
    if (typeof window !== 'undefined' && window.PerformanceObserver) {
      try {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.name.startsWith('auth.')) {
              this.recordMeasurement(entry.name, entry.duration);
            }
          }
        });
        observer.observe({ entryTypes: ['measure'] });
      } catch (error) {
        console.warn('Performance Observer not available:', error);
      }
    }
  }

  /**
   * Start timing an operation
   */
  startOperation(operationName) {
    const startMark = `auth.${operationName}.start`;

    if (typeof window !== 'undefined' && window.performance) {
      // Use high-resolution timestamp
      window.performance.mark(startMark);
    }

    return {
      operationName,
      startTime: Date.now(),
      startMark,
      memoryStart: this.getMemoryUsage()
    };
  }

  /**
   * End timing an operation and record metrics
   */
  endOperation(operation, success = true, metadata = {}) {
    const endMark = `auth.${operation.operationName}.end`;
    const measureName = `auth.${operation.operationName}`;

    if (typeof window !== 'undefined' && window.performance) {
      window.performance.mark(endMark);

      try {
        window.performance.measure(measureName, operation.startMark, endMark);
      } catch (error) {
        // Fallback if marks don't exist
        const duration = Date.now() - operation.startTime;
        this.recordMeasurement(operation.operationName, duration, success, metadata);
      }
    } else {
      // Fallback timing
      const duration = Date.now() - operation.startTime;
      this.recordMeasurement(operation.operationName, duration, success, metadata);
    }

    // Track memory usage
    const memoryEnd = this.getMemoryUsage();
    const memoryDelta = memoryEnd - operation.memoryStart;

    if (memoryDelta > 0) {
      this.recordMemoryUsage(operation.operationName, memoryDelta);
    }
  }

  /**
   * Record a performance measurement
   */
  recordMeasurement(operationName, duration, success = true, metadata = {}) {
    // Initialize array if needed
    if (!this.metrics.operations.has(operationName)) {
      this.metrics.operations.set(operationName, []);
    }

    // Record the measurement
    const measurement = {
      operation: operationName,
      duration: Math.round(duration * 100) / 100, // Round to 2 decimal places
      timestamp: Date.now(),
      success,
      ...metadata
    };

    this.metrics.operations.get(operationName).push(measurement);

    // Update statistics
    this.stats.totalOperations++;

    // Check if operation exceeded budget
    const budget = this.budgets[operationName] || this.budgets.overall;
    if (duration > budget) {
      this.stats.slowOperations++;
      this.metrics.slowOperations.push({
        ...measurement,
        budget,
        exceeded: duration - budget
      });

      console.warn(
        `⚠️ Performance Budget Exceeded: ${operationName} took ${duration}ms (budget: ${budget}ms)`
      );
    }

    // Track errors
    if (!success) {
      this.stats.errors++;
      this.metrics.errors.push(measurement);
    }

    // Update average
    this.updateAverageTime();

    // Clean up old metrics (keep last 1000 per operation)
    const operations = this.metrics.operations.get(operationName);
    if (operations.length > 1000) {
      operations.splice(0, operations.length - 1000);
    }
  }

  /**
   * Get memory usage (if available)
   */
  getMemoryUsage() {
    if (typeof window !== 'undefined' && window.performance && window.performance.memory) {
      return window.performance.memory.usedJSHeapSize;
    }
    return 0;
  }

  /**
   * Record memory usage
   */
  recordMemoryUsage(operationName, bytes) {
    this.metrics.memory.push({
      operation: operationName,
      bytes,
      timestamp: Date.now()
    });

    // Keep only last 100 memory measurements
    if (this.metrics.memory.length > 100) {
      this.metrics.memory.splice(0, this.metrics.memory.length - 100);
    }
  }

  /**
   * Update average operation time
   */
  updateAverageTime() {
    let totalTime = 0;
    let totalCount = 0;

    for (const [, measurements] of this.metrics.operations) {
      for (const measurement of measurements) {
        totalTime += measurement.duration;
        totalCount++;
      }
    }

    this.stats.averageTime = totalCount > 0 ? totalTime / totalCount : 0;
  }

  /**
   * Get performance statistics for a specific operation
   */
  getOperationStats(operationName) {
    const measurements = this.metrics.operations.get(operationName);
    if (!measurements || measurements.length === 0) {
      return null;
    }

    const durations = measurements.map(m => m.duration).sort((a, b) => a - b);
    const sum = durations.reduce((a, b) => a + b, 0);

    return {
      operation: operationName,
      count: durations.length,
      min: durations[0],
      max: durations[durations.length - 1],
      average: sum / durations.length,
      p50: this.getPercentile(durations, 50),
      p95: this.getPercentile(durations, 95),
      p99: this.getPercentile(durations, 99),
      budget: this.budgets[operationName] || this.budgets.overall,
      withinBudget: this.getPercentile(durations, 95) <= (this.budgets[operationName] || this.budgets.overall)
    };
  }

  /**
   * Get percentile value from sorted array
   */
  getPercentile(sortedArray, percentile) {
    const index = Math.ceil((percentile / 100) * sortedArray.length) - 1;
    return sortedArray[Math.max(0, index)];
  }

  /**
   * Get overall performance report
   */
  getReport() {
    const report = {
      summary: {
        totalOperations: this.stats.totalOperations,
        averageTime: Math.round(this.stats.averageTime * 100) / 100,
        slowOperations: this.stats.slowOperations,
        errorRate: this.stats.totalOperations > 0
          ? (this.stats.errors / this.stats.totalOperations) * 100
          : 0,
        withinBudget: this.stats.averageTime <= this.budgets.overall
      },
      operations: {},
      slowestOperations: [],
      recommendations: []
    };

    // Get stats for each operation
    for (const [operationName] of this.metrics.operations) {
      const stats = this.getOperationStats(operationName);
      if (stats) {
        report.operations[operationName] = stats;

        // Track slowest operations
        if (stats.p95 > stats.budget) {
          report.slowestOperations.push({
            operation: operationName,
            p95: stats.p95,
            budget: stats.budget,
            exceeded: stats.p95 - stats.budget
          });
        }
      }
    }

    // Sort slowest operations
    report.slowestOperations.sort((a, b) => b.exceeded - a.exceeded);

    // Generate recommendations
    if (report.summary.averageTime > this.budgets.overall) {
      report.recommendations.push(
        `Average operation time (${report.summary.averageTime}ms) exceeds budget (${this.budgets.overall}ms)`
      );
    }

    if (report.slowestOperations.length > 0) {
      report.recommendations.push(
        `${report.slowestOperations.length} operations consistently exceed their performance budgets`
      );
    }

    if (report.summary.errorRate > 1) {
      report.recommendations.push(
        `Error rate (${report.summary.errorRate.toFixed(2)}%) is above acceptable threshold`
      );
    }

    // Memory recommendations
    const avgMemory = this.getAverageMemoryUsage();
    if (avgMemory > 10 * 1024 * 1024) { // 10MB
      report.recommendations.push(
        `Average memory usage (${(avgMemory / 1024 / 1024).toFixed(2)}MB) is high`
      );
    }

    return report;
  }

  /**
   * Get average memory usage
   */
  getAverageMemoryUsage() {
    if (this.metrics.memory.length === 0) return 0;
    const sum = this.metrics.memory.reduce((a, b) => a + b.bytes, 0);
    return sum / this.metrics.memory.length;
  }

  /**
   * Clear all metrics
   */
  clear() {
    this.metrics.operations.clear();
    this.metrics.memory = [];
    this.metrics.errors = [];
    this.metrics.slowOperations = [];
    this.stats = {
      totalOperations: 0,
      slowOperations: 0,
      errors: 0,
      averageTime: 0
    };
  }

  /**
   * Export metrics for analysis
   */
  exportMetrics() {
    return {
      timestamp: Date.now(),
      metrics: {
        operations: Object.fromEntries(this.metrics.operations),
        memory: this.metrics.memory,
        errors: this.metrics.errors,
        slowOperations: this.metrics.slowOperations
      },
      stats: this.stats,
      report: this.getReport()
    };
  }
}

// Create singleton instance
const authPerformanceMonitor = new AuthPerformanceMonitor();

// Export for use in other modules
export default authPerformanceMonitor;